/**
 *
 * Survey
 *
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import _classNames from 'classnames';
import _every from 'lodash/every';
import _flatten from 'lodash/flatten';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import {
  makeSelectAnonymousId,
  makeSelectAuth,
  makeSelectProfile,
} from 'containers/Auth/selectors';
import {
  makeSelectAICCUser,
  makeSelectClientDetails,
  makeSelectIsEndingSession,
  makeSelectClinicMode,
} from 'containers/Main/selectors';
import { useFirestore } from 'react-redux-firebase';
import { Route, Switch, useLocation, useHistory } from 'react-router-dom';
import { emitter } from '@marvelapp/react-ab-test';
import { MuiThemeProvider, makeStyles } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { getQuestionVisibility } from 'containers/Assessment/utils';
import useSiteConfigSelector from 'components/useSiteConfigSelector';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { getLocalData } from 'utils/localDataStore';
import Mixpanel from 'utils/mixpanelService';
import { getTakenAssessments } from 'containers/Assessments/actions';
import SurveyContext from './SurveyContext';
import ActionButtons from './ActionButtons';
import PageIndicator from './PageIndicator';
import Questions from './Questions';
import {
  getAssessmentQuestionReferral,
  getClientDemographicsQuestions,
  getCustomQuestions,
  getErrors,
  getSurveyPages,
  isPrevPageVisible,
  parseCurrentState,
} from './utils';
import { isEmbedded, windowScrollTo } from '../../utils/embedded';

const useStyles = makeStyles({
  root: {
    background: '#fff',
    borderRadius: 3,
    height: '100%',
  },
  backdrop: {
    filter: 'blur(6px)',
  },
});

const HAS_ERROR_TOAST_ID = 'MISSING_ANSWER_ERROR';

const Survey = ({
  model,
  id,
  onComplete,
  onStart,
  isSignedIn,
  theme,
  slug,
  assessmentName,
  temporaryAnswers,
  isTemporaryAnswersLoaded,
  shouldResume,
  assessmentPath,
}) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const [answers, setAnswers] = useState({});
  const hasLoaded = useRef(false);
  const autoScrollEnabled = useRef(false);
  const [error, setError] = useState([]);
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const activeVariant = emitter.getActiveVariant('main-menu');
  const [siteCopy] = useSiteCopySelector(['assessment']);
  const [siteConfig] = useSiteConfigSelector(['Age and Gender Mappings']);
  const auth = useSelector(makeSelectAuth());
  const sessionAnonymousId = useSelector(makeSelectAnonymousId());
  const clientDetails = useSelector(makeSelectClientDetails());
  const profile = useSelector(makeSelectProfile());
  const aiccUser = useSelector(makeSelectAICCUser());
  const isEndingSession = useSelector(makeSelectIsEndingSession());
  const clinicMode = useSelector(makeSelectClinicMode());
  const dispatch = useDispatch();
  const isAICC = _get(clientDetails, 'aicc', false);
  const userIdTemp = getLocalData('userIdTemp');
  let userId;
  if (isAICC) {
    userId = _get(aiccUser, 'user_id');
  } else if (auth.isLoaded) {
    if (auth.isEmpty) {
      userId = clinicMode
        ? sessionAnonymousId || userIdTemp
        : userIdTemp || sessionAnonymousId;
    } else {
      userId = auth.uid;
    }
  }
  const firestore = useFirestore();
  const referrals = useSelector(state => state.main.referrals);
  const takenAssessments = useSelector(
    state => state.assessments.takenAssessments,
  );

  useEffect(() => {
    dispatch(getTakenAssessments());
  }, []);

  useEffect(() => {
    if (shouldResume !== null && shouldResume === false) {
      setAnswers({});
    }
  }, [shouldResume]);

  useEffect(() => {
    const mobileOffset = isMobile ? 0 : 16;
    if (
      shouldResume !== null &&
      isTemporaryAnswersLoaded &&
      !hasLoaded.current
    ) {
      const surveyWrapper = document.getElementById('survey-wrapper');
      const header = document.getElementById('app-header');
      const defaultTop =
        window.scrollY +
        (surveyWrapper.getBoundingClientRect().top -
          header.offsetHeight -
          mobileOffset);

      const isInProgress =
        shouldResume && !_isEmpty(_get(temporaryAnswers, slug));
      if (isInProgress) {
        const inProgressAnswers = JSON.parse(temporaryAnswers[slug].answers);
        setAnswers(inProgressAnswers);

        const pages = _get(model, 'pages', []).filter(page => {
          const conditionallyVisible = getQuestionVisibility(
            page,
            inProgressAnswers,
          );
          return conditionallyVisible;
        });
        const inProgressPage = temporaryAnswers[slug].page || 1;
        if (inProgressPage <= pages.length) {
          if (inProgressPage > 1)
            navigate(`${assessmentPath}/page${inProgressPage}`);

          const answersKeys = Object.keys(inProgressAnswers).filter(key =>
            Array.isArray(inProgressAnswers[key])
              ? !_isEmpty(inProgressAnswers[key])
              : _has(inProgressAnswers, key),
          );
          const elementsToInspect = [];
          pages.forEach(page => {
            page.elements.forEach(el => {
              if (el.type === 'panel') {
                const conditionallyVisible = getQuestionVisibility(el, answers);
                if (conditionallyVisible && !!el.elements)
                  elementsToInspect.push(...el.elements);
              } else {
                elementsToInspect.push(el);
              }
            });
          });

          let firstUnansweredQuestion;
          let i = 0;
          while (i < elementsToInspect.length && !firstUnansweredQuestion) {
            const el = elementsToInspect[i];
            if (el) {
              const conditionallyVisible = getQuestionVisibility(
                el,
                inProgressAnswers,
              );
              if (conditionallyVisible && !answersKeys.includes(el.name)) {
                firstUnansweredQuestion = el.name;
              } else if (
                conditionallyVisible &&
                (el.type === 'matrix' || el.type === 'matrixdropdown')
              ) {
                if (inProgressAnswers[el.name]) {
                  const matrixAnswersKeys = Object.keys(
                    inProgressAnswers[el.name],
                  );

                  const matrixQuestionsErrors = [];
                  el.rows.forEach(row => {
                    if (!matrixAnswersKeys.includes(row.value))
                      matrixQuestionsErrors.push(row.value);
                  });
                  if (!_isEmpty(matrixQuestionsErrors)) {
                    firstUnansweredQuestion = el.name;
                  }
                } else {
                  firstUnansweredQuestion = el.name;
                }
              }
            }
            i += 1;
          }
          const firstUnansweredQuestionPage = pages.reduce(
            (value, page, index) => {
              const elementsNames = [];
              page.elements.forEach(element => {
                if (element.type === 'panel') {
                  elementsNames.push(...element.elements.map(el => el.name));
                } else {
                  elementsNames.push(element.name);
                }
              });
              if (elementsNames.includes(firstUnansweredQuestion)) return index;
              return value;
            },
            pages.length - 1,
          );

          if (firstUnansweredQuestionPage === inProgressPage - 1) {
            if (firstUnansweredQuestion) {
              setTimeout(() => {
                const question = document.getElementById(
                  `q-${firstUnansweredQuestion}`,
                );

                const top = defaultTop + _get(question, 'offsetTop', 0);
                windowScrollTo(top);
              }, 300);
            } else {
              setTimeout(() => {
                windowScrollTo(defaultTop);
              }, 300);
            }
          } else if (!_isEmpty(inProgressAnswers)) {
            const newError = getErrors(
              pages,
              inProgressPage - 1,
              inProgressAnswers,
              isMobile,
            );

            if (newError.length === 0) {
              firestore
                .collection('assessment_temporary_answers')
                .doc(userId)
                .set(
                  { [slug]: { page: firstUnansweredQuestionPage + 1 } },
                  { merge: true },
                );
              navigate(
                `${assessmentPath}/page${firstUnansweredQuestionPage + 1}`,
              );

              setTimeout(() => {
                const newPageQuestion = document.getElementById(
                  `q-${firstUnansweredQuestion}`,
                );
                windowScrollTo(defaultTop + newPageQuestion.offsetTop);
              }, 300);
            }
          } else {
            setTimeout(() => windowScrollTo(defaultTop), 100);
          }

          hasLoaded.current = true;
        } else {
          setTimeout(() => windowScrollTo(defaultTop), 100);
        }
      }
    } else if (shouldResume === null) {
      setTimeout(() => {
        const resumeOverlay = document.getElementById(
          'resume-overlay-container',
        );
        const header = document.getElementById('app-header');
        const { top } = resumeOverlay.getBoundingClientRect();
        windowScrollTo(window.scrollY + (top - header.offsetHeight - 32));
      }, 100);
    }
  }, [
    activeVariant,
    history,
    isTemporaryAnswersLoaded,
    model,
    slug,
    temporaryAnswers,
  ]);

  useEffect(() => {
    if (!_isEmpty(answers) && !isEndingSession) {
      const answersString = JSON.stringify(answers);

      firestore
        .collection('assessment_temporary_answers')
        .doc(userId)
        .set({ [slug]: { answers: answersString } }, { merge: true });
      hasLoaded.current = true;
    }
  }, [answers, firestore, slug, userId]);

  useEffect(() => {
    if (toast.isActive(HAS_ERROR_TOAST_ID)) {
      toast.dismiss(HAS_ERROR_TOAST_ID);
    }
  }, [answers]);

  useEffect(() => {
    if (autoScrollEnabled.current) {
      const surveyWrapper = document.getElementById('survey-wrapper');
      const header = document.getElementById('app-header');
      const top =
        window.scrollY +
        (surveyWrapper.getBoundingClientRect().top -
          header.offsetHeight -
          (isMobile ? 0 : 16));
      setTimeout(() => windowScrollTo(top), 100);
    }
    if (location) {
      const pageNo = location.pathname.split('/').slice(-1);
      Mixpanel.track('Assessment Question Page Loaded and Viewed', {
        name: assessmentName,
        page: pageNo[0].replace(/\D/g, ''),
        entryId: id,
      });
    }
  }, [location]);

  useEffect(() => {
    if (!_isEmpty(error)) {
      const name = error[0];
      const el = document.getElementById(`q-${name}`);

      if (el) {
        const surveyWrapper = document.getElementById('survey-wrapper');
        const header = document.getElementById('app-header');
        const top =
          window.scrollY +
          (surveyWrapper.getBoundingClientRect().top -
            header.offsetHeight -
            (isMobile ? 0 : 16)) +
          el.offsetTop;
        windowScrollTo(top);
      }

      if (!toast.isActive(HAS_ERROR_TOAST_ID))
        toast(_get(siteCopy, 'pageCopy.missingAnswersError', ''), {
          type: 'error',
          toastId: HAS_ERROR_TOAST_ID,
          position: 'bottom-left',
          closeOnClick: true,
          autoClose: 12000,
        });
      Mixpanel.track('Assessment Question Page - Missed Question', {
        name: assessmentName,
        questionNames: error,
        entryId: id,
      });
    }
  }, [error]);

  const assessmentQuestionsReferral = useMemo(
    () => getAssessmentQuestionReferral(referrals, id, clientDetails),
    [referrals, clientDetails],
  );

  const customQuestions = useMemo(
    () =>
      getCustomQuestions({
        referral: assessmentQuestionsReferral,
        assessmentId: id,
        takenAssessments,
        siteConfig,
        clientShortName: clientDetails.shortName,
        profile,
      }),
    [assessmentQuestionsReferral, takenAssessments],
  );

  const clientDemographicsQuestions = getClientDemographicsQuestions(
    clientDetails,
  );

  const pages = getSurveyPages(
    model,
    answers,
    customQuestions,
    clientDemographicsQuestions,
  );

  const routes = useMemo(
    () =>
      pages.map((_, index) => (
        <Route
          key={`${slug}-page${index + 1}`}
          exact
          path={`${assessmentPath}/page${index + 1}`}
          render={() => (
            <>
              <PageIndicator progress={index} size={pages.length} />
              <Questions currentPage={index} pages={pages} />
              <ActionButtons
                currentPage={index}
                totalPages={pages.length}
                isSignedIn={isSignedIn}
                onPreviousClick={handlePreviousClick}
                assessmentName={assessmentName}
                assessmentId={id}
                slug={slug}
              />
            </>
          )}
        />
      )),
    [pages],
  );

  if (_isEmpty(pages) || !isTemporaryAnswersLoaded) return null;

  const handleAnswerChange = (name, value) =>
    setAnswers(update(answers, { $merge: { [name]: value } }));

  const handleMatrixAnswerChange = (name, innerName, value) => {
    let matrixAnswers = answers[name];
    if (!matrixAnswers) matrixAnswers = {};
    const newMatrixAnswers = update(matrixAnswers, {
      $merge: { [innerName]: value },
    });
    setAnswers(update(answers, { $merge: { [name]: newMatrixAnswers } }));
  };

  const handleMatrixCheckboxAnswerChange = (name, innerName, value) => {
    let matrixAnswers = answers[name];
    if (!matrixAnswers) matrixAnswers = {};

    let checkboxAnswers = matrixAnswers[innerName];
    if (!checkboxAnswers) checkboxAnswers = [];
    let newCheckboxAnswers;
    if (checkboxAnswers.includes(value)) {
      const index = checkboxAnswers.indexOf(value);
      newCheckboxAnswers = update(checkboxAnswers, {
        $splice: [[index, 1]],
      });
    } else {
      newCheckboxAnswers = update(checkboxAnswers, {
        $push: [value],
      });
    }

    const newMatrixAnswers = update(matrixAnswers, {
      $merge: { [innerName]: newCheckboxAnswers },
    });
    setAnswers(update(answers, { $merge: { [name]: newMatrixAnswers } }));
  };

  const handleCheckboxChange = (name, value, possibleValues = []) => {
    let checkboxAnswers = answers[name];
    const otherAnswerKey = `other-option/${name}`;
    let shouldClearOtherAnswer = false;
    let newCheckboxAnswers;

    if (!checkboxAnswers) checkboxAnswers = [];
    if (
      value === 'all' &&
      !_isEmpty(possibleValues) &&
      checkboxAnswers.includes(value)
    ) {
      shouldClearOtherAnswer = true;
      newCheckboxAnswers = [];
    } else if (checkboxAnswers.includes(value)) {
      shouldClearOtherAnswer = true;

      const index = checkboxAnswers.indexOf(value);
      newCheckboxAnswers = update(checkboxAnswers, {
        $splice: [[index, 1]],
      });
    } else if (value === 'all' || value === 'none') {
      newCheckboxAnswers = [value];
    } else if (
      !_isEmpty(possibleValues) &&
      _every(possibleValues, possibleValue =>
        checkboxAnswers
          .filter(item => item !== 'all' && item !== 'none')
          .concat(value)
          .includes(possibleValue),
      )
    ) {
      newCheckboxAnswers = update(
        checkboxAnswers.filter(item => item !== 'all' && item !== 'none'),
        {
          $push: [value, 'all'],
        },
      );
    } else {
      newCheckboxAnswers = update(
        checkboxAnswers.filter(item => item !== 'all' && item !== 'none'),
        {
          $push: [value],
        },
      );
    }

    const ops = {
      $merge: {
        [name]: newCheckboxAnswers,
      },
    };
    if (shouldClearOtherAnswer) {
      ops.$unset = [otherAnswerKey];
    }
    setAnswers(update(answers, ops));
  };

  const handlePreviousClick = page => {
    autoScrollEnabled.current = true;

    let prevPage = page;
    while (prevPage > 1 && !isPrevPageVisible(pages, prevPage, answers)) {
      prevPage -= 1;
    }

    navigate(`${assessmentPath}/page${prevPage}`);

    firestore
      .collection('assessment_temporary_answers')
      .doc(userId)
      .set({ [slug]: { page } }, { merge: true });

    if (page === 2) {
      const surveyWrapper = document.getElementById('survey-wrapper');
      const header = document.getElementById('app-header');
      const top =
        window.scrollY +
        (surveyWrapper.getBoundingClientRect().top -
          header.offsetHeight -
          (isMobile ? 0 : 20));
      setTimeout(() => windowScrollTo(top), 100);
    }
  };

  const handleActionClick = ({
    type = '',
    pageNo = 0,
    shouldSendEmail = false,
  }) => {
    autoScrollEnabled.current = true;

    const newError = getErrors(pages, pageNo, answers, isMobile);

    if (newError.length > 0) {
      setError(newError);
    } else {
      if (error.length > 0) setError([]);

      const removableKeys = [];
      _flatten(pages.map(page => page.elements)).forEach(element => {
        const conditionallyVisible = getQuestionVisibility(element, answers);
        if (element.type === 'panel' && !conditionallyVisible) {
          removableKeys.push(...element.elements.map(el => el.name));
        } else if (!conditionallyVisible) removableKeys.push(element.name);
      });
      const cleanAnswers = update(answers, { $unset: removableKeys });
      setAnswers(cleanAnswers);

      if (pageNo === 0) {
        onStart();
      }
      if (pageNo === pages.length - 1) {
        const completeAnswers = {
          ...cleanAnswers,
        };
        customQuestions.answered.forEach(answer => {
          completeAnswers[answer.name] = answer.answerValue;
        });
        onComplete(completeAnswers, type, shouldSendEmail);
      } else {
        const [nextPage] = parseCurrentState(pages, pageNo, cleanAnswers);

        firestore
          .collection('assessment_temporary_answers')
          .doc(userId)
          .set({ [slug]: { page: pageNo + nextPage } }, { merge: true });
        navigate(`${assessmentPath}/page${pageNo + nextPage}`);
      }
    }
  };

  const navigate = path => {
    if (isEmbedded()) {
      history.push(path);
    } else {
      history.replace(path);
    }
  };
  return (
    <SurveyContext.Provider
      value={{
        answers,
        error,
        onAnswerChange: handleAnswerChange,
        onMatrixChange: handleMatrixAnswerChange,
        onMatrixCheckboxChange: handleMatrixCheckboxAnswerChange,
        onCheckboxChange: handleCheckboxChange,
        onActionClick: handleActionClick,
      }}
    >
      <MuiThemeProvider theme={theme}>
        <div
          className={_classNames(classes.root, {
            [classes.backdrop]: shouldResume === null,
          })}
        >
          <Switch>{routes}</Switch>
        </div>
      </MuiThemeProvider>
    </SurveyContext.Provider>
  );
};

Survey.propTypes = {
  isSignedIn: PropTypes.bool,
  model: PropTypes.object,
  onComplete: PropTypes.func,
  onStart: PropTypes.func,
  theme: PropTypes.object,
  slug: PropTypes.string,
  id: PropTypes.string,
  assessmentName: PropTypes.string,
  temporaryAnswers: PropTypes.object,
  isTemporaryAnswersLoaded: PropTypes.bool,
  shouldResume: PropTypes.bool,
};

export default Survey;
